import { useRef, useState } from "react";
import axios from "axios";
import { fetchUploadUrl } from "../../apicalls/notificationFunctions";

const ALLOWED_MIME_TYPES = ["application/pdf"] as const;
const ALLOWED_MIME_PREFIXES = ["image/"] as const;

function isAllowedFileType(file: File): boolean {
  return (
    (ALLOWED_MIME_TYPES as readonly string[]).includes(file.type) ||
    ALLOWED_MIME_PREFIXES.some((prefix) => file.type.startsWith(prefix))
  );
}

export type SelectedFile = {
  chipkey: string;
  name: string;
  file: File;
  key?: string;
  signedUrl?: string;
};

export type Attachment = {
  key: string;
  name: string;
  source: "existing" | "new";
  isRemoved: boolean;
  uploadProgress?: number;
};

export type UploadResult = {
  status: "success" | "error";
  keys: string[];
  errors: string[];
};

type UseNotificationUploadOptions = {
  existingAttachmentKeys?: string[];
};

export function useNotificationUpload(
  options: UseNotificationUploadOptions = {},
) {
  const { existingAttachmentKeys } = options;
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [progressBarValues, setProgressBarValues] = useState<
    Map<string, number>
  >(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [invalidFileErrors, setInvalidFileErrors] = useState<string[]>([]);
  const [removedAttachmentKeys, setRemovedAttachmentKeys] = useState<
    Set<string>
  >(new Set());

  const filesRemainingRef = useRef(0);
  const attachmentS3Keys = useRef<string[]>([]);
  const uploadErrorsRef = useRef<string[]>([]);
  const onSaveReadyRef = useRef<((result: UploadResult) => void) | null>(null);

  const computeAttachments = (): Attachment[] => {
    const newAttachments: Attachment[] = selectedFiles.map((f) => ({
      key: f.chipkey,
      name: f.name,
      source: "new",
      isRemoved: false,
      uploadProgress: progressBarValues.get(f.chipkey) ?? 0,
    }));

    const existingAttachments: Attachment[] = (
      existingAttachmentKeys ?? []
    ).map((key) => ({
      key,
      name: key,
      source: "existing",
      isRemoved: removedAttachmentKeys.has(key),
    }));

    return [...newAttachments, ...existingAttachments];
  };

  const updateProgress = (chipkey: string, value: number) => {
    setProgressBarValues((prev) => new Map(prev.set(chipkey, value)));
  };

  const registerFinishedUpload = () => {
    filesRemainingRef.current -= 1;
    if (filesRemainingRef.current === 0) {
      setUploadErrors(uploadErrorsRef.current);
      const status = uploadErrorsRef.current.length > 0 ? "error" : "success";
      onSaveReadyRef.current?.({
        status,
        keys: attachmentS3Keys.current,
        errors: uploadErrorsRef.current,
      });
    }
  };

  const uploadFile = async (item: SelectedFile) => {
    try {
      const { key, signedUrl } = await fetchUploadUrl(item.name);

      await axios.put(signedUrl, item.file, {
        onUploadProgress: (e: {
          lengthComputable: boolean;
          loaded: number;
          total: number;
        }) => {
          if (e.lengthComputable) {
            updateProgress(
              item.chipkey,
              Math.round((e.loaded / e.total) * 100),
            );
          }
        },
      });

      attachmentS3Keys.current.push(key);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("useNotificationUpload: error on upload", err);
      const message = err instanceof Error ? err.message : String(err);

      uploadErrorsRef.current = [
        ...uploadErrorsRef.current,
        `"${item.name}": ${message}`,
      ];
    } finally {
      registerFinishedUpload();
    }
  };

  const triggerSave = (onReady: (result: UploadResult) => void) => {
    setIsSaving(true);
    setUploadErrors([]);
    setInvalidFileErrors([]);
    attachmentS3Keys.current = [];
    uploadErrorsRef.current = [];
    onSaveReadyRef.current = onReady;

    if (selectedFiles.length > 0) {
      filesRemainingRef.current = selectedFiles.length;
      selectedFiles.forEach((f) => {
        uploadFile(f).catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.error(
            "useNotificationUpload: unexpected error on upload",
            err,
          );
        });
      });
    } else {
      onReady({
        status: "success",
        keys: [],
        errors: [],
      });
    }
  };

  const onFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.files) return;
    const curr = [...selectedFiles];
    const newInvalidNames: string[] = [];

    Array.from(ev.target.files).forEach((file) => {
      if (isAllowedFileType(file)) {
        if (!curr.some((f) => f.chipkey === file.name)) {
          curr.push({
            chipkey: file.name,
            name: file.name,
            file: new File([file], file.name, { type: file.type }),
          });
        }
      } else {
        newInvalidNames.push(file.name);
      }
    });

    setInvalidFileErrors(newInvalidNames);
    setSelectedFiles(curr);
    const input = ev.target;
    input.value = "";
  };

  const onChipDelete = (chipkey: string) => {
    setSelectedFiles((files) => files.filter((f) => f.chipkey !== chipkey));
  };

  const onToggleRemoveAttachment = (key: string) => {
    setRemovedAttachmentKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const resetSaving = () => setIsSaving(false);

  return {
    attachments: computeAttachments(),
    isSaving,
    uploadErrors,
    invalidFileErrors,
    triggerSave,
    onFileChange,
    onChipDelete,
    onToggleRemoveAttachment,
    resetSaving,
  };
}
